import React, { useState, useEffect } from 'react';
import { X, Calendar, User, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { User as UserType } from '../types/user';
import { getPotentialManagers, reassignUser } from '../../services/usersapi';
import { toast } from 'sonner';

interface UserReassignmentModalProps {
  user: UserType | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UserReassignmentModal: React.FC<UserReassignmentModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [newManagerUserId, setNewManagerUserId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [availableManagers, setAvailableManagers] = useState<UserType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchAvailableManagers();
      // Reset form
      setNewManagerUserId('');
      setEffectiveDate('');
    }
  }, [isOpen, user]);

  const fetchAvailableManagers = async () => {
    if (!user?.id) return;
    setIsLoadingManagers(true);
    try {
      const response = await getPotentialManagers(user.id);
      const managers =
        response?.status === 'success'
          ? (response.data?.potentialManagers ?? []).filter(
              (manager: { id: string }) => manager.id !== user.id
            )
          : [];
      setAvailableManagers(managers);
    } catch (error) {
      console.error('Failed to fetch managers:', error);
      toast.error('Failed to load available managers');
    } finally {
      setIsLoadingManagers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !newManagerUserId) {
      toast.error('Please select a new manager');
      return;
    }

    setIsLoading(true);
    try {
      const payload: any = {
        newManagerUserId
      };

      if (effectiveDate) {
        payload.effectiveDate = new Date(effectiveDate).toISOString();
      }

      await reassignUser(user.id, payload);
      
      const selectedManager = availableManagers.find(m => m.id === newManagerUserId);
      toast.success(`${user.username} has been successfully reassigned to ${selectedManager?.username}`);
      
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to reassign user:', error);
      toast.error(error.response?.data?.message || 'Failed to reassign user');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5" />
            Reassign User
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Current User</Label>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <User className="w-4 h-4 text-gray-500" />
                <div>
                  <span className="font-medium">{user.username}</span>
                  <span className="text-sm text-gray-500 ml-2">({user.role})</span>
                  {user.location && (
                    <div className="text-xs text-gray-400">{user.location}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newManager">New Manager *</Label>
              <Select 
                value={newManagerUserId} 
                onValueChange={setNewManagerUserId}
                disabled={isLoadingManagers}
              >
                <SelectTrigger>
                  <SelectValue 
                    placeholder={
                      isLoadingManagers 
                        ? "Loading managers..." 
                        : availableManagers.length === 0
                        ? "No managers available"
                        : "Select new manager"
                    } 
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableManagers.map((manager) => (
                    <SelectItem key={manager.id} value={manager.id}>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{manager.username}</span>
                          <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                            {manager.role}
                          </span>
                        </div>
                        {manager.location && (
                          <span className="text-xs text-gray-500">{manager.location}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableManagers.length === 0 && !isLoadingManagers && (
                <p className="text-xs text-red-500">
                  No available managers found for this user&apos;s role.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="effectiveDate">Effective Date (Optional)</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="effectiveDate"
                  type="datetime-local"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="pl-10"
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
              <p className="text-xs text-gray-500">
                Leave empty for immediate effect. Future dates will schedule the reassignment.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !newManagerUserId || isLoadingManagers}
                className="flex-1 bg-[#EC6E51] hover:bg-[#EC6E51]/90"
              >
                {isLoading ? 'Reassigning...' : 'Reassign User'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserReassignmentModal;